import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { PickerSheet } from '@/features/wardrobe/components/PickerSheet';
import { Thumb, Badge, LoadingState, inputClass } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { SLOT_CATEGORIES, STATUS_LABELS } from '@/lib/constants';
import { cn } from '@/lib/utils';
import type { Item, FitSlot } from '@/lib/types';

interface ItemPickerSheetProps {
  isOpen: boolean;
  slot: FitSlot | null;
  onClose: () => void;
  onSelect: (item: Item) => void;
}

// Bottom sheet that lists in-closet items filtered by the categories valid for a slot.
export function ItemPickerSheet({ isOpen, slot, onClose, onSelect }: ItemPickerSheetProps) {
  const { data, loading } = useApi<{ items: Item[] }>(isOpen ? '/items?status=in-closet' : null);
  const [query, setQuery] = useState('');

  const items = useMemo(() => {
    if (!data?.items || !slot) return [];
    const categories = SLOT_CATEGORIES[slot] ?? [];
    const q = query.trim().toLowerCase();
    return data.items.filter((item) => {
      if (!categories.includes(item.category)) return false;
      if (!q) return true;
      return (
        item.name.toLowerCase().includes(q) ||
        (item.brand?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [data, slot, query]);

  return (
    <PickerSheet isOpen={isOpen} onClose={onClose} title="Select Item">
      <div className="px-2 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search items…"
            className={cn(inputClass, 'pl-10')}
          />
        </div>
      </div>

      {loading ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-10">No matching items in your closet.</p>
      ) : (
        <div className="space-y-2 px-2">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item)}
              className="w-full flex items-center gap-3 p-3 bg-secondary rounded-xl hover:bg-secondary/80 transition-colors text-left"
            >
              <Thumb url={item.primaryPhotoUrl} alt={item.name} className="w-12 h-12 rounded-lg flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground leading-tight line-clamp-1">{item.name}</p>
                {item.brand && <p className="text-xs text-muted-foreground">{item.brand}</p>}
              </div>
              {item.status !== 'in-closet' && (
                <Badge tone="warning">{STATUS_LABELS[item.status] ?? item.status}</Badge>
              )}
            </button>
          ))}
        </div>
      )}
    </PickerSheet>
  );
}
